import {
  AlertTriangle,
  Box,
  Edit3,
  House,
  Minus,
  Plus,
  RotateCcw,
  ShoppingCart,
  Trash2,
  Weight,
} from 'lucide-react'
import { useState } from 'react'

import ImageComponent from '@/components/misc/Images/Image'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InvoiceFullProductData } from '@/interfaces/billing'
import { SelledProductsInterface } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useAreasQuery } from '@/hooks/business/useAreasQuery'

// Action interfaces (copied from ProductManager)
interface AddProductAction {
  type: 'ADD'
  tempId: string
  product: InvoiceFullProductData
  quantity: number
  priceUnitary: { amount: number; codeCurrency: string }
  productionAreaId?: number
}

interface ModifyProductAction {
  type: 'MODIFY'
  originalProduct: SelledProductsInterface
  newQuantity: number
  newPrice: number
  currency: string
}

interface DeleteProductAction {
  type: 'DELETE'
  originalProduct: SelledProductsInterface
  quantity: number
}

interface ProductManagerActions {
  addActions: AddProductAction[]
  modifyActions: ModifyProductAction[]
  deleteActions: DeleteProductAction[]
}

interface ProductActionsViewProps {
  existingProducts: SelledProductsInterface[]
  actions: ProductManagerActions
  onRemoveAddAction: (_tempId: string) => void
  onUpdateAddQuantity: (_tempId: string, _newQuantity: number) => void
  onUpdateAddPrice: (
    _tempId: string,
    _priceUnitary: { amount: number; codeCurrency: string }
  ) => void
  onUpdateAddProductionArea: (
    _tempId: string,
    _productionAreaId: number
  ) => void
  onModifyProduct: (
    _originalProduct: SelledProductsInterface,
    _newQuantity: number,
    _newPrice: number
  ) => void
  onDeleteProduct: (
    _originalProduct: SelledProductsInterface,
    _quantityToDelete?: number
  ) => void
  products: InvoiceFullProductData[]
  onAddProduct: (_product: InvoiceFullProductData, _quantity?: number) => void
  currencyTotals: Record<string, number>
  totalWeight: number
  totalVolume: number
  getAvailableStock: (_productId: number) => number
  onFocusProduct?: (_productId: number) => void
  documentType?: 'factura' | 'prefactura'
  isTemporaryChanges?: boolean
  className?: string
  hasValidationErrors?: boolean
  productsWithoutAreaSelection?: AddProductAction[]
  selectedPointOfSale: number
}

// --- Helpers de decimales ---

const getDecimalPlaces = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  const [, decimals = ''] = value.toString().split('.')
  const trimmed = decimals.replace(/0+$/, '')
  return Math.min(trimmed.length, 3)
}

const getQuantityDecimalsForProduct = (
  product: { saleByWeight?: boolean; measure?: string },
  sampleQuantity?: number
): number => {
  const sampleDecimals =
    sampleQuantity !== undefined ? getDecimalPlaces(sampleQuantity) : 0

  const prefersWeightPrecision =
    !!product.saleByWeight || (product.measure && product.measure !== 'UNIT')

  const preferredDecimals = prefersWeightPrecision ? 3 : 2

  return Math.max(preferredDecimals, sampleDecimals)
}

// Limpiar string numérico y limitar decimales
const normalizeDecimalInput = (value: string, maxDecimals: number): string => {
  let cleaned = value.replace(/[^0-9.]/g, '')

  // Solo un punto
  const parts = cleaned.split('.')
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('')
  }

  // Limitar decimales
  if (cleaned.includes('.')) {
    const [intPart, decPart] = cleaned.split('.')
    cleaned = intPart + '.' + (decPart ? decPart.slice(0, maxDecimals) : '')
  }

  // Quitar ceros iniciales tipo 0012 -> 12 (pero respetar 0.xx)
  if (/^0[0-9]/.test(cleaned)) {
    cleaned = cleaned.replace(/^0+/, '')
  }

  return cleaned
}

export const ProductActionsView = ({
  existingProducts,
  actions,
  onRemoveAddAction,
  onUpdateAddQuantity,
  onUpdateAddPrice,
  onUpdateAddProductionArea,
  onModifyProduct,
  onDeleteProduct,
  products,
  onAddProduct,
  currencyTotals,
  totalWeight,
  totalVolume,
  getAvailableStock,
  onFocusProduct,
  documentType,
  isTemporaryChanges,
  className,
  hasValidationErrors,
  productsWithoutAreaSelection,
  selectedPointOfSale,
}: ProductActionsViewProps) => {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  const { areas, isLoadingAreas, isErrorAreas } = useAreasQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  // State to track raw input values during editing to preserve user intent
  const [inputValues, setInputValues] = useState<Record<string, string>>({})

  // Validación genérica para cantidad (con decimales configurables)
  const validateQuantityInput = (value: string, maxDecimals = 2): string => {
    return normalizeDecimalInput(value, maxDecimals)
  }

  // Parse quantity with better handling of edge cases during editing
  const parseQuantityInput = (value: string): number => {
    const trimmed = value.trim()

    // Handle empty or just decimal point
    if (trimmed === '' || trimmed === '.') {
      return 1 // Default quantity
    }

    // Handle cases like "0." or ".5"
    if (trimmed.endsWith('.')) {
      const parsed = parseFloat(trimmed.slice(0, -1))
      return isNaN(parsed) ? 1 : parsed
    }

    if (trimmed.startsWith('.')) {
      const parsed = parseFloat('0' + trimmed)
      return isNaN(parsed) ? 1 : parsed
    }

    const parsed = parseFloat(trimmed)
    return isNaN(parsed) || parsed <= 0 ? 1 : parsed
  }

  // Helper function to format quantity display with proper decimal places
  const formatQuantity = (quantity: number, decimalPlaces = 2): string => {
    const precision = Math.max(decimalPlaces, 0)
    return quantity % 1 === 0
      ? quantity.toString()
      : quantity.toFixed(precision).replace(/\.?0+$/, '')
  }

  // Get display value for quantity input - either the raw input value or formatted quantity
  const getQuantityDisplayValue = (
    key: string,
    quantity: number,
    decimalPlaces = 2
  ): string => {
    if (inputValues[key] !== undefined) {
      return inputValues[key]
    }
    return formatQuantity(quantity, decimalPlaces)
  }

  // Handle quantity input change with better editing experience
  const handleQuantityChange = (
    key: string,
    value: string,
    onUpdate: (_newQuantity: number) => void,
    maxDecimals = 2
  ) => {
    const cleanedValue = validateQuantityInput(value, maxDecimals)

    setInputValues((prev) => ({ ...prev, [key]: cleanedValue }))

    if (cleanedValue && cleanedValue !== '.' && !cleanedValue.endsWith('.')) {
      const parsedQuantity = parseQuantityInput(cleanedValue)
      onUpdate(parsedQuantity)
    }
  }

  // Handle quantity input blur - finalize the value
  const handleQuantityBlur = (
    key: string,
    onUpdate: (_newQuantity: number) => void
  ) => {
    const rawValue = inputValues[key]
    if (rawValue !== undefined) {
      const parsedQuantity = parseQuantityInput(rawValue)
      onUpdate(parsedQuantity)
      setInputValues((prev) => {
        const newState = Object.fromEntries(
          Object.entries(prev).filter(([k]) => k !== key)
        )
        return newState
      })
    }
  }

  const formatPrice = (price: number, decimalPlaces = 2): string => {
    if (!Number.isFinite(price)) {
      return ''
    }

    const precision = Math.max(decimalPlaces, 0)
    return price % 1 === 0
      ? price.toString()
      : price.toFixed(precision).replace(/\.?0+$/, '')
  }

  const getPriceDisplayValue = (
    key: string,
    price: number | undefined,
    decimalPlaces = 2
  ): string => {
    if (inputValues[key] !== undefined) {
      return inputValues[key]
    }

    if (price === undefined || price === null || isNaN(price)) {
      return ''
    }

    return formatPrice(price, decimalPlaces)
  }

  const handlePriceChange = (
    key: string,
    value: string,
    maxDecimals: number,
    onUpdate: (_newPrice: number) => void
  ) => {
    const cleanedValue = normalizeDecimalInput(value, maxDecimals)

    setInputValues((prev) => ({ ...prev, [key]: cleanedValue }))

    if (cleanedValue && cleanedValue !== '.' && !cleanedValue.endsWith('.')) {
      const parsedPrice = parseFloat(cleanedValue)
      if (!isNaN(parsedPrice)) {
        onUpdate(parsedPrice)
      }
    }
  }

  const handlePriceBlur = (
    key: string,
    maxDecimals: number,
    onUpdate: (_newPrice: number) => void,
    fallbackValue: number
  ) => {
    const rawValue = inputValues[key]
    if (rawValue === undefined) {
      return
    }

    const cleanedValue = normalizeDecimalInput(rawValue, maxDecimals)

    let finalValue = fallbackValue

    if (cleanedValue && cleanedValue !== '.') {
      finalValue = cleanedValue.endsWith('.')
        ? parseFloat(cleanedValue.slice(0, -1))
        : parseFloat(cleanedValue)
    }

    if (isNaN(finalValue)) {
      finalValue = fallbackValue
    }

    onUpdate(finalValue)

    setInputValues((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = prev
      return rest
    })
  }

  // Calculate total quantity of items instead of just counting different products
  const totalQuantity = (() => {
    let total = 0

    // Calculate from existing products (considering modifications and deletions)
    existingProducts.forEach((product) => {
      const deleteAction = actions.deleteActions.find(
        (action) => action.originalProduct.id === product.id
      )
      const modifyAction = actions.modifyActions.find(
        (action) => action.originalProduct.id === product.id
      )

      let effectiveQuantity = product.quantity

      if (modifyAction) {
        effectiveQuantity = modifyAction.newQuantity
      }

      if (deleteAction) {
        effectiveQuantity = Math.max(
          0,
          effectiveQuantity - deleteAction.quantity
        )
      }

      total += effectiveQuantity
    })

    // Add quantities from add actions
    actions.addActions.forEach((action) => {
      total += action.quantity
    })

    return total
  })()

  const totalActionItems =
    actions.addActions.length +
    actions.modifyActions.length +
    actions.deleteActions.length +
    existingProducts.length

  // Handle clicking on existing product row to focus it in stock
  const handleExistingProductClick = (
    product: SelledProductsInterface,
    event: React.MouseEvent
  ) => {
    if ((event.target as HTMLElement).closest('button')) {
      return
    }

    onFocusProduct?.(product.productId)
  }

  const handlePartialDelete = (product: SelledProductsInterface) => {
    onDeleteProduct(product, 1)
  }

  const handleFullDelete = (product: SelledProductsInterface) => {
    onDeleteProduct(product)
  }

  const handleAddOne = (product: SelledProductsInterface) => {
    const fullProduct = products.find(
      (p: InvoiceFullProductData) => p.id === product.productId
    )

    if (fullProduct) {
      onAddProduct(fullProduct)
    }
  }

  const handleAdjustDeleteQuantity = (
    deleteAction: DeleteProductAction,
    adjustment: number
  ) => {
    const newDeleteQuantity = deleteAction.quantity + adjustment

    if (newDeleteQuantity <= 0) {
      onDeleteProduct(deleteAction.originalProduct, 0)
      return
    }

    const maxDeleteQuantity = deleteAction.originalProduct.quantity
    const finalDeleteQuantity = Math.min(newDeleteQuantity, maxDeleteQuantity)

    onDeleteProduct(deleteAction.originalProduct, finalDeleteQuantity)
  }

  const getRemainingQuantity = (
    originalProduct: SelledProductsInterface
  ): number => {
    const deleteAction = actions.deleteActions.find(
      (action) => action.originalProduct.id === originalProduct.id
    )
    return deleteAction
      ? originalProduct.quantity - deleteAction.quantity
      : originalProduct.quantity
  }

  const isProductBeingEdited = (productId: number): boolean => {
    return (
      actions.modifyActions.some(
        (action) => action.originalProduct.id === productId
      ) ||
      actions.deleteActions.some(
        (action) => action.originalProduct.id === productId
      )
    )
  }

  const handleRestoreProduct = (product: SelledProductsInterface) => {
    onDeleteProduct(product, 0)
    onModifyProduct(product, product.quantity, product.priceUnitary.amount)
  }

  const ProductionAreaSelector = ({
    areas,
    selectedAreaId,
    onAreaChange,
  }: {
    areas: Pick<{ id: number; name: string }, 'id' | 'name'>[]
    selectedAreaId?: number
    onAreaChange: (_areaId: number) => void
  }) => {
    return (
      <div className="w-64 p-2">
        <div className="mb-3">
          <h4 className="text-sm font-medium text-foreground">
            Seleccionar Área de Producción
          </h4>
          <p className="text-xs text-muted-foreground">
            Elige el área donde se preparará este producto
          </p>
        </div>
        <RadioGroup
          onValueChange={(value) => onAreaChange(Number(value))}
          value={selectedAreaId?.toString() || ''}
        >
          {areas.map((area) => (
            <div className="flex items-center space-x-2" key={area.id}>
              <RadioGroupItem
                className="text-primary"
                id={`area-${area.id}`}
                value={area.id.toString()}
              />
              <Label
                className="flex-1 cursor-pointer py-1 text-sm font-normal"
                htmlFor={`area-${area.id}`}
              >
                {area.name}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
    )
  }

  // eslint-disable-next-line react/prop-types
  const currentArea = areas.find(
    (item) => item.id === Number(selectedPointOfSale)
  )

  return (
    <div className={cn('flex h-full flex-col space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Productos y Acciones</h3>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <ShoppingCart className="h-4 w-4" />
          <span>
            {totalQuantity} elemento{totalQuantity !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Products and Actions List */}
      <ScrollArea className="relative h-[100px] flex-grow rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">Producto</TableHead>
              <TableHead className="w-[110px] text-right">
                Precio Unit.
              </TableHead>
              <TableHead className="w-[90px] text-right">Cantidad</TableHead>
              <TableHead className="w-[120px] text-right">Subtotal</TableHead>
              <TableHead className="w-[100px] text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totalActionItems === 0 ? (
              <TableRow>
                <TableCell className="h-40 text-center" colSpan={5}>
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <ShoppingCart className="h-8 w-8 text-muted-foreground/60" />
                    <p className="mt-2">No hay productos seleccionados</p>
                    <p className="text-sm">
                      Añade productos desde el inventario
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* All Existing Products (always visible) */}
                {existingProducts.map((product) => {
                  const modifyAction = actions.modifyActions.find(
                    (action) => action.originalProduct.id === product.id
                  )
                  const isBeingEdited = isProductBeingEdited(product.id)
                  const isBeingModified = !!modifyAction

                  const displayQuantity = modifyAction
                    ? modifyAction.newQuantity
                    : product.quantity
                  const displayPrice = modifyAction
                    ? modifyAction.newPrice
                    : product.priceUnitary.amount
                  const displaySubtotal = displayQuantity * displayPrice

                  const fullProduct = products.find(
                    (p) => p.id === product.productId
                  )

                  const quantityDecimalPlaces = getQuantityDecimalsForProduct(
                    {
                      saleByWeight: fullProduct?.saleByWeight,
                      measure: fullProduct?.measure,
                    },
                    displayQuantity
                  )

                  const priceDecimalPlaces = 2
                  const modifyPriceInputKey = `modify-price-${product.id}`

                  const productExists = existingProducts.some(
                    (existingProduct) =>
                      existingProduct.productId ===
                      products.find((p) => p.id === product.productId)?.id
                  )

                  return (
                    <TableRow
                      className={cn(
                        'cursor-pointer bg-background transition-colors',
                        isBeingEdited
                          ? 'border-l-4 border-l-orange-400 bg-orange-50/50'
                          : 'hover:bg-muted/50'
                      )}
                      key={`existing-${product.id}`}
                      onClick={(e) => handleExistingProductClick(product, e)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <ImageComponent
                            className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg"
                            hash={product.image?.blurHash}
                            src={product.image?.src}
                          />
                          <div className="flex min-w-0 flex-col">
                            <div className="flex items-center gap-2">
                              {isBeingEdited && (
                                <Edit3 className="h-3 w-3 text-orange-600" />
                              )}
                              <span className="text-start font-medium">
                                {product.name}
                                {isBeingEdited ? (
                                  <span className="text-xs font-medium text-orange-600">
                                    {' '}
                                    (editando)
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {' '}
                                    (existente)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex flex-col justify-between text-start text-xs text-muted-foreground">
                              <span className="italic">
                                {product.variation
                                  ? product.variation.name
                                  : '-'}
                              </span>
                              <div className="flex items-start gap-2">
                                {product.totalWeight && (
                                  <div className="flex items-center gap-1">
                                    <Weight className="inline h-3 w-3" />
                                    <span className="min-w-fit">
                                      {product.totalWeight} kg
                                    </span>
                                  </div>
                                )}
                                {product.totalCubaje && (
                                  <div className="flex items-center gap-1">
                                    <Box className="inline h-3 w-3" />
                                    <span className="min-w-fit">
                                      {product.totalCubaje} m³
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {isBeingModified ? (
                          <Input
                            className="h-7 w-20 text-right text-sm"
                            inputMode="decimal"
                            onBlur={() => {
                              handlePriceBlur(
                                modifyPriceInputKey,
                                priceDecimalPlaces,
                                (finalPrice) => {
                                  onModifyProduct(
                                    product,
                                    modifyAction?.newQuantity,
                                    finalPrice
                                  )
                                },
                                0
                              )
                            }}
                            onChange={(e) => {
                              handlePriceChange(
                                modifyPriceInputKey,
                                e.target.value,
                                priceDecimalPlaces,
                                (newPrice) => {
                                  onModifyProduct(
                                    product,
                                    modifyAction?.newQuantity,
                                    newPrice
                                  )
                                }
                              )
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.select()}
                            placeholder={'0.00'}
                            type="text"
                            value={getPriceDisplayValue(
                              modifyPriceInputKey,
                              modifyAction?.newPrice,
                              priceDecimalPlaces
                            )}
                          />
                        ) : (
                          <span className="text-sm">
                            {displayPrice != null &&
                            !isNaN(Number(displayPrice))
                              ? Number(displayPrice)
                                  .toFixed(priceDecimalPlaces)
                                  .replace(/\.?0+$/, '')
                              : '0'}{' '}
                            {product.priceUnitary.codeCurrency}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isBeingModified ? (
                          <Input
                            className="h-7 w-16 text-right text-sm"
                            inputMode="decimal"
                            onBlur={() => {
                              handleQuantityBlur(
                                `modify-${product.id}`,
                                (newQuantity) => {
                                  const availableStock = getAvailableStock(
                                    product.productId
                                  )
                                  const maxQuantity =
                                    availableStock + product.quantity
                                  const finalQuantity = Math.min(
                                    newQuantity,
                                    maxQuantity
                                  )
                                  onModifyProduct(
                                    product,
                                    finalQuantity,
                                    modifyAction?.newPrice
                                  )
                                }
                              )
                            }}
                            onChange={(e) => {
                              handleQuantityChange(
                                `modify-${product.id}`,
                                e.target.value,
                                (newQuantity) => {
                                  const availableStock = getAvailableStock(
                                    product.productId
                                  )
                                  const maxQuantity =
                                    availableStock + product.quantity
                                  const finalQuantity = Math.min(
                                    newQuantity,
                                    maxQuantity
                                  )
                                  const safePrice =
                                    modifyAction?.newPrice ??
                                    product.priceUnitary.amount

                                  onModifyProduct(
                                    product,
                                    finalQuantity,
                                    safePrice
                                  )
                                },
                                quantityDecimalPlaces
                              )
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.select()}
                            placeholder="1"
                            type="text"
                            value={getQuantityDisplayValue(
                              `modify-${product.id}`,
                              modifyAction?.newQuantity ?? product.quantity,
                              quantityDecimalPlaces
                            )}
                          />
                        ) : (
                          <span className="text-sm">
                            {formatQuantity(
                              displayQuantity,
                              quantityDecimalPlaces
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {displaySubtotal.toFixed(2)}{' '}
                        {product.priceUnitary.codeCurrency}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {isBeingEdited ? (
                            <Button
                              className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRestoreProduct(product)
                              }}
                              size="icon"
                              title="Restaurar a estado original"
                              variant="ghost"
                            >
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          ) : (
                            <>
                              {product.quantity > 1 && (
                                <Button
                                  className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePartialDelete(product)
                                  }}
                                  size="icon"
                                  title="Eliminar 1 unidad"
                                  variant="ghost"
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                className="h-6 w-6 text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleFullDelete(product)
                                }}
                                size="icon"
                                title="Eliminar producto"
                                variant="ghost"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          {productExists && (
                            <Button
                              className="h-6 w-6 text-green-600 hover:bg-green-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleAddOne(product)
                              }}
                              size="icon"
                              title="Añadir 1 unidad"
                              variant="ghost"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}

                {actions.deleteActions.length > 0 && (
                  <TableRow>
                    <TableCell className="p-0" colSpan={5}>
                      <Separator className="bg-red-200" />
                      <div className="bg-red-50/30 px-3 py-1 text-xs font-medium text-red-700">
                        <Minus className="mr-1 inline h-3 w-3" />
                        Producto{actions.deleteActions.length > 1 ? 's' : ''} a
                        eliminar
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Deleted Products */}
                {actions.deleteActions
                  .slice()
                  .sort((a, b) => a.originalProduct.id - b.originalProduct.id)
                  .map((action) => {
                    const remainingQuantity = getRemainingQuantity(
                      action.originalProduct
                    )
                    const isFullDeletion =
                      action.quantity === action.originalProduct.quantity
                    const canIncreaseDelete =
                      action.quantity < action.originalProduct.quantity

                    const fullProduct = products.find(
                      (p) => p.id === action.originalProduct.productId
                    )

                    const deleteQuantityDecimals =
                      getQuantityDecimalsForProduct(
                        {
                          saleByWeight: fullProduct?.saleByWeight,
                          measure: fullProduct?.measure,
                        },
                        action.originalProduct.quantity
                      )

                    return (
                      <TableRow
                        className="border-l-4 border-l-red-500 bg-red-50/50"
                        key={`delete-${action.originalProduct.id}`}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <ImageComponent
                              className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg opacity-50"
                              hash={action.originalProduct.image?.blurHash}
                              src={action.originalProduct.image?.src}
                            />
                            <div className="flex min-w-0 flex-col">
                              <div className="flex items-center gap-2">
                                <Minus className="h-3 w-3 text-red-600" />
                                <span className="font-medium text-red-700">
                                  {action.originalProduct.name}
                                </span>
                                <span className="text-xs font-medium text-red-600">
                                  (eliminando)
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {isFullDeletion ? (
                                  'Eliminación completa'
                                ) : (
                                  <>
                                    Eliminando{' '}
                                    {formatQuantity(
                                      action.quantity,
                                      deleteQuantityDecimals
                                    )}{' '}
                                    de{' '}
                                    {formatQuantity(
                                      action.originalProduct.quantity,
                                      deleteQuantityDecimals
                                    )}
                                    {remainingQuantity > 0 && (
                                      <span className="ml-2 text-green-600">
                                        (Quedan:{' '}
                                        {formatQuantity(
                                          remainingQuantity,
                                          deleteQuantityDecimals
                                        )}
                                        )
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-red-600">
                            -
                            {action.originalProduct.priceUnitary.amount !=
                              null &&
                            !isNaN(
                              Number(action.originalProduct.priceUnitary.amount)
                            )
                              ? Number(
                                  action.originalProduct.priceUnitary.amount
                                ).toFixed(2)
                              : '0.00'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-semibold text-red-600">
                            -
                            {formatQuantity(
                              action.quantity,
                              deleteQuantityDecimals
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <span className="text-red-600">
                            -
                            {(
                              action.quantity *
                              action.originalProduct.priceUnitary.amount
                            ).toFixed(2)}{' '}
                            {action.originalProduct.priceUnitary.codeCurrency}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canIncreaseDelete && (
                              <Button
                                className="h-6 w-6 text-yellow-600 hover:bg-yellow-50"
                                onClick={() => {
                                  if (canIncreaseDelete) {
                                    handleAdjustDeleteQuantity(action, 1)
                                  } else {
                                    handleAdjustDeleteQuantity(
                                      action,
                                      -action.quantity
                                    )
                                  }
                                }}
                                size="icon"
                                title={
                                  canIncreaseDelete
                                    ? 'Eliminar 1 unidad'
                                    : 'Restaurar producto'
                                }
                                variant="ghost"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                            )}
                            <Button
                              className="h-6 w-6 text-green-600 hover:bg-green-50"
                              onClick={() =>
                                handleAdjustDeleteQuantity(action, -1)
                              }
                              size="icon"
                              title="Aumentar 1 unidad"
                              variant="ghost"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}

                {/* Add Actions Separator */}
                {actions.addActions.length > 0 && (
                  <TableRow>
                    <TableCell className="p-0" colSpan={5}>
                      <Separator className="bg-green-200" />
                      <div className="bg-green-50/30 px-3 py-1 text-xs font-medium text-green-700">
                        <Plus className="mr-1 inline h-3 w-3" />
                        Producto{actions.addActions.length > 1 ? 's' : ''} nuevo
                        {actions.addActions.length > 1 ? 's' : ''}
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {/* Add Actions */}
                {actions.addActions.map((action) => {
                  const addQuantityDecimals = getQuantityDecimalsForProduct(
                    action.product,
                    action.quantity
                  )
                  const addPriceDecimals = 2
                  const addPriceInputKey = `add-price-${action.tempId}`

                  return (
                    <TableRow
                      className="border-l-4 border-l-green-500 bg-green-50/50"
                      key={action.tempId}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className="relative flex-shrink-0">
                            <ImageComponent
                              className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg"
                              hash={action.product.images?.[0]?.blurHash}
                              src={action.product.images?.[0]?.src}
                            />
                            {action.product.listProductionAreas.length > 1 && (
                              <div className="absolute -right-2 -top-2">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    {action.productionAreaId ? (
                                      <div
                                        className={cn(
                                          'flex items-center gap-2 rounded-full border transition-all',
                                          'border-green-400 bg-green-200 p-1 text-green-800',
                                          'cursor-pointer hover:bg-green-300 hover:text-green-700'
                                        )}
                                      >
                                        <House className="h-4 w-4" />
                                      </div>
                                    ) : (
                                      <div
                                        className={cn(
                                          'flex items-center gap-2 rounded-full border transition-all',
                                          'border-red-400 bg-red-200 p-1 text-red-800',
                                          'cursor-pointer hover:bg-red-300 hover:text-red-700'
                                        )}
                                      >
                                        <AlertTriangle className="h-4 w-4" />
                                      </div>
                                    )}
                                  </PopoverTrigger>
                                  <PopoverContent className="p-0" side="bottom">
                                    <ProductionAreaSelector
                                      areas={action.product.listProductionAreas}
                                      onAreaChange={(areaId) =>
                                        onUpdateAddProductionArea(
                                          action.tempId,
                                          areaId
                                        )
                                      }
                                      selectedAreaId={action.productionAreaId}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )}
                          </div>

                          <div className="flex min-w-0 flex-col">
                            <div className="flex items-center gap-2">
                              <Plus className="h-3 w-3 flex-shrink-0 text-green-600" />
                              <span className="text-start font-medium">
                                {action.product.name}
                              </span>
                              <span className="text-xs font-medium text-green-600">
                                (nuevo)
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {action.product.netWeight && (
                                <span>
                                  <Weight className="inline h-3 w-3" />{' '}
                                  {action.product.netWeight} kg
                                </span>
                              )}
                              {action.product.cubaje && (
                                <span className="ml-2">
                                  <Box className="inline h-3 w-3" />{' '}
                                  {action.product.cubaje} m³
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            className="h-7 w-20 text-right text-sm"
                            disabled={
                              !currentArea?.allowManualPrice ||
                              isLoadingAreas ||
                              isErrorAreas
                            }
                            inputMode="decimal"
                            onBlur={() => {
                              handlePriceBlur(
                                addPriceInputKey,
                                addPriceDecimals,
                                (finalPrice) => {
                                  onUpdateAddPrice(action.tempId, {
                                    amount: finalPrice,
                                    codeCurrency:
                                      action.priceUnitary.codeCurrency,
                                  })
                                },
                                0
                              )
                            }}
                            onChange={(e) => {
                              handlePriceChange(
                                addPriceInputKey,
                                e.target.value,
                                addPriceDecimals,
                                (newAmount) => {
                                  onUpdateAddPrice(action.tempId, {
                                    amount: newAmount,
                                    codeCurrency:
                                      action.priceUnitary.codeCurrency,
                                  })
                                }
                              )
                            }}
                            onFocus={(e) => e.target.select()}
                            placeholder={'0.00'}
                            type="text"
                            value={getPriceDisplayValue(
                              addPriceInputKey,
                              action.priceUnitary.amount ?? undefined,
                              addPriceDecimals
                            )}
                          />
                          <Select
                            onValueChange={(currency) => {
                              onUpdateAddPrice(action.tempId, {
                                amount: action.priceUnitary.amount,
                                codeCurrency: currency,
                              })
                            }}
                            value={action.priceUnitary.codeCurrency}
                          >
                            <SelectTrigger className="h-7 w-16 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="CUP">CUP</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Input
                            className="h-7 w-16 text-right text-sm"
                            inputMode="decimal"
                            onBlur={() => {
                              handleQuantityBlur(
                                `add-${action.tempId}`,
                                (newQuantity) =>
                                  onUpdateAddQuantity(
                                    action.tempId,
                                    newQuantity
                                  )
                              )
                            }}
                            onChange={(e) => {
                              handleQuantityChange(
                                `add-${action.tempId}`,
                                e.target.value,
                                (newQuantity) =>
                                  onUpdateAddQuantity(
                                    action.tempId,
                                    newQuantity
                                  ),
                                addQuantityDecimals
                              )
                            }}
                            onFocus={(e) => e.target.select()}
                            placeholder="1"
                            type="text"
                            value={getQuantityDisplayValue(
                              `add-${action.tempId}`,
                              action.quantity,
                              addQuantityDecimals
                            )}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {(action.quantity * action.priceUnitary.amount).toFixed(
                          2
                        )}{' '}
                        {action.priceUnitary.codeCurrency}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          className="h-6 w-6 text-destructive"
                          onClick={() => onRemoveAddAction(action.tempId)}
                          size="icon"
                          title="Eliminar"
                          variant="ghost"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </>
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      {/* Summary */}
      {totalActionItems > 0 && (
        <div className="flex-shrink-0 rounded-md bg-muted p-4">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between font-medium">
              <span>Subtotal:</span>
              <div className="flex flex-col gap-1">
                {Object.entries(currencyTotals).map(([currency, total]) => (
                  <p key={currency}>
                    {total.toFixed(2)} {currency}
                  </p>
                ))}
              </div>
            </div>
            {totalWeight > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Weight className="h-4 w-4" /> Peso Total:
                </span>
                <span>{totalWeight.toFixed(2)} kg</span>
              </div>
            )}
            {totalVolume > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Box className="h-4 w-4" /> Volumen Total:
                </span>
                <span>{totalVolume.toFixed(3)} m³</span>
              </div>
            )}

            {(actions.addActions.length > 0 ||
              actions.modifyActions.length > 0 ||
              actions.deleteActions.length > 0) && (
              <>
                <Separator className="my-2" />
                <div className="pb-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>
                      {isTemporaryChanges
                        ? `Impacto planificado en ${
                            documentType === 'factura'
                              ? 'stock real'
                              : 'stock reservado'
                          }:`
                        : documentType
                          ? `Impacto en inventario (${
                              documentType === 'factura'
                                ? 'stock real'
                                : 'stock reservado'
                            }):`
                          : 'Impacto en inventario:'}
                    </span>
                  </div>
                  <div className="mt-1 space-y-1">
                    {actions.addActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-green-600">
                          •{' '}
                          {actions.addActions.reduce(
                            (sum, action) => sum + action.quantity,
                            0
                          )}{' '}
                          unidad(es){' '}
                          {isTemporaryChanges ? 'planificadas' : 'agregadas'}
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'a reservar' : 'reservadas'})`
                            : ''}
                        </span>
                      </div>
                    )}
                    {actions.modifyActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-blue-600">
                          • {actions.modifyActions.length} producto(s)
                          modificado(s)
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'reserva planificada' : 'reserva'})`
                            : ''}
                        </span>
                      </div>
                    )}
                    {actions.deleteActions.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-red-600">
                          •{' '}
                          {actions.deleteActions.reduce(
                            (sum, action) => sum + action.quantity,
                            0
                          )}{' '}
                          unidad(es){' '}
                          {isTemporaryChanges ? 'a liberar' : 'liberadas'}
                          {documentType === 'prefactura'
                            ? ` (${isTemporaryChanges ? 'des-reservar' : 'des-reservadas'})`
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {hasValidationErrors &&
              productsWithoutAreaSelection &&
              productsWithoutAreaSelection.length > 0 && (
                <Alert
                  className="flex flex-row items-center border-red-200 bg-red-50"
                  variant="destructive"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle className="mt-0.5 text-sm font-medium">
                    Quedan productos sin seleccionar un área de producción
                  </AlertTitle>
                </Alert>
              )}
          </div>
        </div>
      )}
    </div>
  )
}
